/**
 * Image label annotation extension handler.
 *
 * Image labels are simple boolean flags per series. label_key goes into
 * annotation.label_name, boolean value (0/1) into annotation.label_value.
 */
import { BaseExtensionHandler } from "../_base.ts"
import type { ExtensionManifest, SerializedAnnotation, StoredAnnotation } from "../_interface.ts"
import { createImageLabelSchema, imageLabelConfigSchema, updateImageLabelSchema } from "./schema.ts"

export class ImageLabelHandler extends BaseExtensionHandler {
  readonly type = "image_label"
  readonly manifest: ExtensionManifest = {
    type: "image_label",
    label: "Image Label",
    scope: "series",
    storage: "structured",
    supportsLinking: false
  }

  getConfigSchema() {
    return imageLabelConfigSchema
  }

  getCreateSchema() {
    return createImageLabelSchema
  }

  getUpdateSchema() {
    return updateImageLabelSchema
  }

  serializeForDb(apiData: unknown): SerializedAnnotation {
    const data = apiData as {
      label_key?: string
      value?: boolean
      mapped_series_ids?: string[]
    }

    const eav: SerializedAnnotation["data"] = []
    if (data.mapped_series_ids && data.mapped_series_ids.length > 0) {
      eav.push({
        field_key: "mapped_series_ids",
        value_text: JSON.stringify(data.mapped_series_ids)
      })
    }

    return {
      annotation: {
        label_name: data.label_key ?? null,
        label_value: data.value != null ? (data.value ? 1 : 0) : null
      },
      data: eav,
      files: []
    }
  }

  deserializeFromDb(stored: StoredAnnotation): unknown {
    const mappedRow = stored.data.find((d) => d.field_key === "mapped_series_ids")
    let mapped_series_ids: string[] | undefined
    if (mappedRow?.value_text) {
      try {
        mapped_series_ids = JSON.parse(mappedRow.value_text)
      } catch {
        mapped_series_ids = undefined
      }
    }

    return {
      id: stored.annotation.id,
      submission_id: stored.annotation.submission_id,
      project_module_id: stored.annotation.project_module_id,
      series_id: stored.annotation.series_id,
      label_key: stored.annotation.label_name,
      value: stored.annotation.label_value,
      ...(mapped_series_ids ? { mapped_series_ids } : {}),
      created_at: stored.annotation.created_at,
      updated_at: stored.annotation.updated_at
    }
  }

  getExportColumns(templateSchema: unknown): string[] {
    const schema = templateSchema as {
      options?: Array<{ value: string }>
      series_mapping?: boolean
    }
    const base = (schema.options ?? []).map((o) => `label_${o.value}`)
    if (schema.series_mapping) {
      return [...base, ...(schema.options ?? []).map((o) => `label_${o.value}_series`)]
    }
    return base
  }

  toExportRow(annotations: StoredAnnotation[], templateSchema: unknown): Record<string, string | number> {
    const columns = this.getExportColumns(templateSchema)
    const result: Record<string, string | number> = {}

    for (const col of columns) {
      result[col] = col.endsWith("_series") ? "" : 0
    }

    for (const a of annotations) {
      const key = `label_${a.annotation.label_name}`
      if (key in result) {
        result[key] = a.annotation.label_value ?? 0
      }

      const seriesKey = `${key}_series`
      if (seriesKey in result) {
        const mappedRow = a.data.find((d) => d.field_key === "mapped_series_ids")
        if (mappedRow?.value_text) {
          try {
            const ids = JSON.parse(mappedRow.value_text) as string[]
            result[seriesKey] = ids.join(",")
          } catch {
            // skip
          }
        }
      }
    }

    return result
  }
}
